import axios from "axios";

// Use same-origin for API calls. Emergent's ingress routes /api/* to the
// backend automatically on every domain (preview + custom). This avoids the
// CORS preflight failure that happens when REACT_APP_BACKEND_URL (baked at
// build time) points to a different domain than the one serving the page.
const BACKEND =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

// Persistent session id stored in localStorage
const SESSION_KEY = "csd_session_id";
export function getSessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = (crypto.randomUUID && crypto.randomUUID()) || Math.random().toString(36).slice(2) + Date.now();
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

const client = axios.create({ baseURL: API });
client.interceptors.request.use((config) => {
  config.headers["X-Session-Id"] = getSessionId();
  return config;
});

export default client;

export const api = {
  createAgreement: (data) => client.post("/agreements", data).then((r) => r.data),
  getAgreement: (id) => client.get(`/agreements/${id}`).then((r) => r.data),
  updateAgreement: (id, data) => client.put(`/agreements/${id}`, data).then((r) => r.data),
  listAgreements: () => client.get("/agreements").then((r) => r.data),
  decodeVin: (vin) => client.post("/vin/decode", { vin }).then((r) => r.data),
  uploadAttachment: (id, file, category, caption) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    fd.append("caption", caption || "");
    return client
      .post(`/agreements/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  deleteAttachment: (id, attId) => client.delete(`/agreements/${id}/attachments/${attId}`).then((r) => r.data),
  fileUrl: (storage_path) => `${API}/files?path=${encodeURIComponent(storage_path)}`,
  sign: (id, body) => client.post(`/agreements/${id}/sign`, body).then((r) => r.data),
  paymentMock: (id) => client.post(`/agreements/${id}/payment-mock`).then((r) => r.data),
  paymentCheckout: (agreement_id) =>
    client
      .post("/payments/checkout", { agreement_id, origin_url: window.location.origin })
      .then((r) => r.data),
  paymentStatus: (session_id) =>
    client.get(`/payments/status/${session_id}`).then((r) => r.data),
  pdfUrl: (id) => `${API}/agreements/${id}/pdf`,
  samplePdfUrl: () => `${API}/sample-pdf`,
  stateGuidance: (state) => client.get(`/state-guidance/${encodeURIComponent(state)}`).then((r) => r.data),
  sendForSignature: (id) => client.post(`/agreements/${id}/send-for-signature`).then((r) => r.data),
  scanId: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client
      .post(`/agreements/${id}/scan-id`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  shareLink: (id) => client.post(`/agreements/${id}/share-link`).then((r) => r.data),
  emailSigningLink: (id, email) =>
    client.post(`/agreements/${id}/email-signing-link`, { email }).then((r) => r.data),
  getAgreementPublic: (id, token, role) =>
    client.get(`/agreements/${id}/public`, { params: { t: token, role } }).then((r) => r.data),
  updateAgreementPublic: (id, token, role, data) =>
    client.put(`/agreements/${id}/public`, data, { params: { t: token, role } }).then((r) => r.data),
  signPublic: (id, token, role, body) =>
    client.post(`/agreements/${id}/public-sign`, body, { params: { t: token, role } }).then((r) => r.data),

  // ---- Private-sale car value calculator ----
  valuationMakes: () => client.get("/valuation/makes").then((r) => r.data),
  valuationModels: (makeSlug) =>
    client.get(`/valuation/models/${makeSlug}`).then((r) => r.data),
  valuationCalculate: (payload) =>
    client.post("/valuation/calculate", payload).then((r) => r.data),
  valuationEmailReport: (leadId, email) =>
    client.post(`/valuation/${leadId}/email-report`, { email }).then((r) => r.data),
  valuationTrackBuilderClick: (leadId) =>
    client.post(`/valuation/${leadId}/track-builder-click`).then((r) => r.data),

  // ---- Lightweight backend pageview tracker (admin funnel) ----
  // Fire-and-forget — never block render on failure.
  trackPageview: (path) =>
    client.post("/track/pageview", { path }).catch(() => null),

  // ---- Contact form ----
  submitContact: ({ name, email, subject, body }) =>
    client.post("/contact/messages", { name, email, subject, body }).then((r) => r.data),
};
